import React, { useEffect, useRef } from 'react';
import mermaid from 'mermaid';
import { visaFlowchartCode } from './visaFlowchartData';

/**
 * Mermaid 流程图组件
 *
 * 功能：
 * 1. 在 React 中安全地渲染 Mermaid 流程图
 * 2. 处理 React 18+ StrictMode 双重渲染问题
 * 3. 管理 Mermaid 初始化和清理
 * 4. 支持普通视图和全屏视图两种尺寸
 *
 * Props：
 * - isFullscreen: boolean - 是否为全屏模式（默认 false）
 */
const MermaidFlowchart = ({ isFullscreen = false }) => {
  const containerRef = useRef(null);
  const hasRendered = useRef(false);

  useEffect(() => {
    // 在 StrictMode 开发环境中避免重复初始化
    if (hasRendered.current) {
      return;
    }

    const renderDiagram = async () => {
      if (!containerRef.current) return;

      try {
        // 初始化 Mermaid 配置
        mermaid.initialize({
          startOnLoad: true,
          theme: 'default',
          securityLevel: 'loose', // 允许点击链接
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true
          }
        });

        // 清理容器中的旧内容
        containerRef.current.innerHTML = '';

        // 创建包含 Mermaid 代码的 div
        const diagramDiv = document.createElement('div');
        diagramDiv.className = 'mermaid';
        diagramDiv.textContent = visaFlowchartCode;
        containerRef.current.appendChild(diagramDiv);

        // 触发 Mermaid 渲染
        await mermaid.contentLoaded();

        hasRendered.current = true;
      } catch (error) {
        console.error('Mermaid rendering error:', error);
      }
    };

    renderDiagram();

    // 清理函数（用于卸载组件时）
    return () => {
      // 不需要额外清理，Mermaid 会自动管理 DOM
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`w-full bg-white rounded-lg p-4 border-2 border-gray-100 overflow-x-auto ${
        isFullscreen ? 'min-h-[85vh]' : ''
      }`}
      style={{
        minHeight: isFullscreen ? '85vh' : '600px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* 加载提示 */}
      <div className="text-gray-400 text-center">
        <p>正在加载流程图...</p>
      </div>
    </div>
  );
};

export default MermaidFlowchart;
